import React from "react";
import PropTypes from "prop-types";
import { Button, Col, Form, Overlay, Popover, Row } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";

import Modal from "../../../layout/modal";
import Table from "../../../layout/table";
import Hamburger from "../../../layout/kebab";
import ValueSelect from "../../../layout/select";
import {
  CUSTOM_FIELD_TYPES,
  normalizeCustomFields,
  validateCustomFields,
} from "../../../utils/template-custom-fields";

const emptyField = () => ({
  uuid: uuidv4(),
  name: "",
  caption: "",
  tooltip: "",
  type: "string",
  required: false,
});

const typeLabel = (value) =>
  CUSTOM_FIELD_TYPES.find((item) => item.value === value)?.label || value || "";

class CustomFieldsEditor extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      actionMenu: {
        show: false,
        target: null,
      },
      rowMenu: {
        show: false,
        target: null,
        row: null,
      },
      modal: {
        show: false,
        mode: "add",
        field: emptyField(),
        errors: {},
      },
    };
  }

  get fields() {
    return normalizeCustomFields(this.props.fields);
  }

  get errors() {
    return this.props.errors || validateCustomFields(this.fields).errors;
  }

  openActionMenu = (event) => {
    event.preventDefault();
    this.setState({
      actionMenu: {
        show: true,
        target: event.currentTarget,
      },
    });
  };

  closeActionMenu = () => {
    this.setState({
      actionMenu: {
        show: false,
        target: null,
      },
    });
  };

  openRowMenu = (event, row) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({
      rowMenu: {
        show: true,
        target: event.currentTarget,
        row,
      },
    });
  };

  closeRowMenu = () => {
    this.setState({
      rowMenu: {
        show: false,
        target: null,
        row: null,
      },
    });
  };

  openAddModal = () => {
    this.closeActionMenu();
    this.setState({
      modal: {
        show: true,
        mode: "add",
        field: emptyField(),
        errors: {},
      },
    });
  };

  openEditModal = (row) => {
    this.closeRowMenu();
    this.setState({
      modal: {
        show: true,
        mode: "edit",
        field: {
          uuid: row.uuid,
          name: row.name || "",
          caption: row.caption || "",
          tooltip: row.tooltip || "",
          type: row.type || "string",
          required: Boolean(row.required),
        },
        errors: {},
      },
    });
  };

  closeModal = () => {
    this.setState(prev => ({
      modal: {
        ...prev.modal,
        show: false,
        errors: {},
      },
    }));
  };

  onModalChange = (event) => {
    const { name, value, checked, type } = event.target;
    this.setState(prev => ({
      modal: {
        ...prev.modal,
        field: {
          ...prev.modal.field,
          [name]: type === "checkbox" ? checked : value,
        },
        errors: {
          ...prev.modal.errors,
          [name]: undefined,
        },
      },
    }));
  };

  onModalSave = () => {
    const { mode, field } = this.state.modal;
    const nextFields = mode === "edit"
      ? this.fields.map((item) => item.uuid === field.uuid ? field : item)
      : [...this.fields, field];
    const validation = validateCustomFields(nextFields);
    const fieldErrors = validation.errors?.[field.uuid] || {};

    if (!validation.valid) {
      this.setState(prev => ({
        modal: {
          ...prev.modal,
          errors: fieldErrors,
        },
      }));
      return;
    }

    this.props.onChange?.(validation.fields);
    this.closeModal();
  };

  onDelete = (row) => {
    this.closeRowMenu();
    this.props.onChange?.(this.fields.filter((field) => field.uuid !== row.uuid));
  };

  onDeleteAll = () => {
    this.closeActionMenu();
    this.props.onChange?.([]);
  };

  onOpenFormat = (value, row) => (
    <Button variant="link" className="p-0 align-baseline text-start" onClick={() => this.openEditModal(row)}>
      {value}
    </Button>
  );

  onTypeFormat = (value, row) => (
    <Button variant="link" className="p-0 align-baseline text-start" onClick={() => this.openEditModal(row)}>
      {typeLabel(value)}
    </Button>
  );

  onCaptionFormat = (value, row) => (
    <Button variant="link" className="p-0 align-baseline text-start" onClick={() => this.openEditModal(row)}>
      {value || row.name}
    </Button>
  );

  onTooltipFormat = (value, row) => (
    <Button variant="link" className="p-0 align-baseline text-start" onClick={() => this.openEditModal(row)}>
      {value || ""}
    </Button>
  );

  onRequiredFormat = (value, row) => (
    <Button variant="link" className="p-0 align-baseline text-start" onClick={() => this.openEditModal(row)}>
      {value ? "Yes" : "No"}
    </Button>
  );

  onActionFormat = (value, row) => (
    <Hamburger variant="link" vertical={false} onClick={(event) => this.openRowMenu(event, row)} />
  );

  get headers() {
    return [
      {
        header: "Name",
        sortable: false,
        source: "name",
        className: "text-muted text-small text-uppercase w-25",
        onRender: this.onOpenFormat,
      },
      {
        header: "Caption",
        sortable: false,
        source: "caption",
        className: "text-muted text-small text-uppercase w-25",
        onRender: this.onCaptionFormat,
      },
      {
        header: "Type",
        sortable: false,
        source: "type",
        className: "text-muted text-small text-uppercase w-20",
        onRender: this.onTypeFormat,
      },
      {
        header: "Tooltip",
        sortable: false,
        source: "tooltip",
        className: "text-muted text-small text-uppercase w-20",
        onRender: this.onTooltipFormat,
      },
      {
        header: "Required",
        sortable: false,
        source: "required",
        className: "text-muted text-small text-uppercase w-10",
        onRender: this.onRequiredFormat,
      },
      {
        header: "",
        sortable: false,
        source: "uuid",
        className: "text-muted text-small text-uppercase w-5",
        onRender: this.onActionFormat,
      },
    ];
  }

  renderActionMenu() {
    return (
      <Overlay
        target={this.state.actionMenu.target}
        show={this.state.actionMenu.show}
        placement="bottom-end"
        rootClose
        onHide={this.closeActionMenu}
      >
        {(props) => (
          <Popover {...props}>
            <Popover.Body>
              <Row>
                <Col className="d-flex flex-row mt-1">
                  <button
                    type="button"
                    className="btn btn-link p-0 me-2 btn-sm small text-start"
                    onClick={this.openAddModal}
                  >
                    Add
                  </button>
                </Col>
              </Row>
              <Row>
                <Col className="d-flex flex-row mt-1">
                  <button
                    type="button"
                    className="btn btn-link p-0 me-2 btn-sm small text-start text-danger"
                    onClick={this.onDeleteAll}
                    disabled={!this.fields.length}
                  >
                    Delete All
                  </button>
                </Col>
              </Row>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    );
  }

  renderRowMenu() {
    const row = this.state.rowMenu.row || {};

    return (
      <Overlay
        target={this.state.rowMenu.target}
        show={this.state.rowMenu.show}
        placement="bottom-end"
        rootClose
        onHide={this.closeRowMenu}
      >
        {(props) => (
          <Popover {...props}>
            <Popover.Body>
              <Row>
                <Col className="d-flex flex-row mt-1">
                  <button
                    type="button"
                    className="btn btn-link p-0 me-2 btn-sm small text-start"
                    onClick={() => this.openEditModal(row)}
                  >
                    Open
                  </button>
                </Col>
              </Row>
              <Row>
                <Col className="d-flex flex-row mt-1">
                  <button
                    type="button"
                    className="btn btn-link p-0 me-2 btn-sm small text-start text-danger"
                    onClick={() => this.onDelete(row)}
                  >
                    Delete
                  </button>
                </Col>
              </Row>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    );
  }

  renderModal() {
    const { field, errors, mode } = this.state.modal;

    return (
      <Modal
        show={this.state.modal.show}
        title={mode === "edit" ? "Custom Field" : "Add Custom Field"}
        buttons={[
          {
            caption: "ok",
            onEnabledClick: this.onModalSave,
            variant: "primary",
            enabled: true,
            display: true,
          },
          {
            default: true,
            caption: "cancel",
            onEnabledClick: this.closeModal,
            variant: "secondary",
            enabled: true,
            display: true,
          },
        ]}
      >
        <Row className="g-3 mt-1">
          <Col xs="12">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="name"
                value={field.name || ""}
                isInvalid={Boolean(errors.name)}
                onChange={this.onModalChange}
              />
              <Form.Label>NAME</Form.Label>
              <Form.Control.Feedback type="invalid">
                {errors.name || "Name is required"}
              </Form.Control.Feedback>
            </div>
          </Col>
          <Col xs="12">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="caption"
                value={field.caption || ""}
                onChange={this.onModalChange}
              />
              <Form.Label>CAPTION</Form.Label>
            </div>
          </Col>
          <Col xs="12">
            <div className="mb-3 top-label">
              <ValueSelect
                name="type"
                selected={field.type || "string"}
                onChange={this.onModalChange}
                options={CUSTOM_FIELD_TYPES}
              />
              <Form.Label>TYPE</Form.Label>
            </div>
          </Col>
          <Col xs="12">
            <div className="mb-3 top-label">
              <Form.Control
                as="textarea"
                rows={3}
                name="tooltip"
                value={field.tooltip || ""}
                onChange={this.onModalChange}
              />
              <Form.Label>TOOLTIP</Form.Label>
            </div>
          </Col>
          <Col xs="12">
            <Form.Check
              type="switch"
              id={`custom-field-required-${field.uuid}`}
              name="required"
              label="Required"
              checked={Boolean(field.required)}
              onChange={this.onModalChange}
            />
          </Col>
        </Row>
      </Modal>
    );
  }

  render() {
    return (
      <>
        <Row className="g-3 align-items-center">
          <Col>
            <h2 className="small-title mb-0">Custom Fields</h2>
          </Col>
          <Col xs="auto">
            <Hamburger variant="link" vertical={false} onClick={this.openActionMenu} />
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs="12">
            <Table
              headers={this.headers}
              page={this.fields}
              size={Math.max(this.fields.length, 1)}
              index={0}
              pages={1}
              searchable={false}
              sortable={false}
              sizable={false}
            />
          </Col>
        </Row>
        {this.renderActionMenu()}
        {this.renderRowMenu()}
        {this.renderModal()}
      </>
    );
  }
}

CustomFieldsEditor.propTypes = {
  fields: PropTypes.array,
  errors: PropTypes.object,
  onChange: PropTypes.func,
};

CustomFieldsEditor.defaultProps = {
  fields: [],
  errors: null,
  onChange: undefined,
};

export default CustomFieldsEditor;
